import { useState } from "react";
import { TipoStatusAssinaturaDigital } from "types/dto/groupsign/TipoStatusAssinaturaDigital";
import { FiltroGestaoAssinatura, PeriodoData } from "../types/types";
import { GestaoAssinaturaDTO } from "types/dto/groupsign/GestaoAssinaturaDTO";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import useGetGestaoAssinaturaDigitalDocumentoPaginada from "hooks/groupsign/useGetGestaoAssinaturaDigitalDocumentoPaginada";
import useGetDadosDashboardGestaoAssinatura from "hooks/groupsign/useGetDadosDashboardGestaoAssinatura";
import { zonedTimeToUtc } from "date-fns-tz";
import { FiltroDashboardDTO } from "types/dto/groupsign/FiltroDashboardDTO";

const PARAMETROS_INICIAIS: FiltroGestaoAssinatura = {
  size: 10,
  page: 0,
  sort: "condominio.pessoaJuridica.nomeFantasia,ASC",
  tipoStatusAtivacao: "AMBOS",
  nomeCondominioDocumento: "",
  dataInicio: "",
  dataFim: "",
};

export const useGestaoAssinaturaLogic = () => {
  const [buscarCondominio, setBuscarCondominio] = useState<string>("");
  const [statusAtivacao, setStatusAtivacao] = useState<TipoStatusAssinaturaDigital>("TODOS");
  const [ordenarPorColuna, setOrdenarPorColuna] = useState<string>("condominio.pessoaJuridica.nomeFantasia");
  const [ordenacaoAscOuDesc, setOrdenacaoAscOuDesc] = useState<"ASC" | "DESC">("ASC");
  const [, setSortString] = useState<string>("condominio.pessoaJuridica.nomeFantasia,ASC");
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [dataInicio, setDataInicio] = useState<Date | null>(null);
  const [dataFim, setDataFim] = useState<Date | null>(null);

  const formatarData = (data: Date | null): string => {
    if (!data) return "";
    const dataUTC = zonedTimeToUtc(data, "America/Sao_Paulo");
    return format(dataUTC, "yyyy-MM-dd'T'HH:mm:ssxxx");
  };

  const formatarDataFim = (data: Date | null): string => {
    if (!data) return "";
    return format(data, "yyyy-MM-dd");
  };

  const obterParametros = (): FiltroGestaoAssinatura => {
    return {
      ...PARAMETROS_INICIAIS,
      page: paginaAtual - 1,
      sort: `${ordenarPorColuna},${ordenacaoAscOuDesc}`,
      tipoStatusAtivacao: statusAtivacao === "TODOS" ? "" : statusAtivacao,
      nomeCondominioDocumento: buscarCondominio,
      dataInicio: formatarData(dataInicio),
      dataFim: formatarDataFim(dataFim),
      ...(statusAtivacao !== "TODOS" && { status: statusAtivacao }),
    };
  };

  const parametros = obterParametros();

  const filtroDashboard = {
    nomeCondominioDocumento: buscarCondominio || "",
    dataInicio: formatarData(dataInicio),
    dataFim: formatarDataFim(dataFim),
    status: statusAtivacao !== "TODOS" ? statusAtivacao : "",
  } as FiltroDashboardDTO;

  const {
    data: response,
    isLoading,
    refetch: refetchLista,
  } = useGetGestaoAssinaturaDigitalDocumentoPaginada(parametros);
  const { data: responseDashboard, refetch: refetchDashboard } = useGetDadosDashboardGestaoAssinatura(filtroDashboard);

  const data = response?.content || [];
  const totalPages = response?.totalPages || 1;
  const totalElements = response?.totalElements || 0;
  const totalPendentes = responseDashboard?.pendentesAssinatura || 0;
  const totalAssinados = responseDashboard?.assinados || 0;
  const totalRecusados = responseDashboard?.recusados || 0;
  const totalCancelados = responseDashboard?.cancelados || 0;

  const calcularPeriodoGestoes = (documentos: GestaoAssinaturaDTO[]): PeriodoData | null => {
    if (documentos.length === 0) return null;

    const datasValidas = documentos
      .map((doc) => (doc.dataHoraEnvio ? new Date(doc.dataHoraEnvio) : null))
      .filter((data): data is Date => data !== null && !isNaN(data.getTime()));

    if (datasValidas.length === 0) return null;

    return {
      dataMinima: new Date(Math.min(...datasValidas.map((data) => data.getTime()))),
      dataMaxima: new Date(Math.max(...datasValidas.map((data) => data.getTime()))),
    };
  };

  const formatarPeriodo = (inicio?: Date | null, fim?: Date | null): string | null => {
    if (!inicio && !fim) return null;

    const inicioFormatado = inicio ? format(inicio, "MMMM yyyy", { locale: ptBR }) : null;
    const fimFormatado = fim ? format(fim, "MMMM yyyy", { locale: ptBR }) : null;

    if (inicioFormatado && fimFormatado) {
      return inicioFormatado === fimFormatado ? inicioFormatado : `${inicioFormatado} a ${fimFormatado}`;
    }

    return inicioFormatado || fimFormatado;
  };

  const handleSort = (columnId: string) => {
    const isAsc = ordenarPorColuna === columnId && ordenacaoAscOuDesc === "ASC";
    setOrdenarPorColuna(columnId);
    setOrdenacaoAscOuDesc(isAsc ? "DESC" : "ASC");
  };

  const titulo = formatarPeriodo(dataInicio, dataFim)
    ? `Status de assinatura de ${formatarPeriodo(dataInicio, dataFim)}`
    : "Status de assinatura";

  const refetchAll = async () => {
    await Promise.all([refetchLista(), refetchDashboard()]);
  };

  return {
    buscarCondominio,
    statusAtivacao,
    ordenarPorColuna,
    ordenacaoAscOuDesc,
    paginaAtual,
    dataInicio,
    dataFim,
    obterParametros,
    calcularPeriodoGestoes,
    formatarPeriodo,
    setBuscarCondominio,
    setStatusAtivacao,
    setOrdenarPorColuna,
    setOrdenacaoAscOuDesc,
    setSortString,
    setPaginaAtual,
    setDataInicio,
    setDataFim,
    data,
    totalPages,
    totalElements,
    totalPendentes,
    totalAssinados,
    totalRecusados,
    totalCancelados,
    isLoading,
    refetch: refetchAll,
    handleSort,
    titulo,
  };
};
